/**
 * OpenAI function schemas + prompting instructions for native tools.
 *
 * Ported from the pre-DO-migration Supabase seed
 * (`supabase/database/data/load_tool_native.sql` at commit d199d7693^).
 * The static-catalog migration in #1426 dropped these fields; restoring
 * them here keeps `native-tools.ts` lean (pure metadata) while still
 * feeding the full schema into tool creation and LLM completions.
 *
 * Keyed by `tool_name` (the function name exposed to the LLM).
 */

export interface NativeToolSchema {
  /** Full OpenAI function schema: { name, description, strict?, parameters: { type, properties, required, additionalProperties } } */
  tool_config: Record<string, unknown>;
  /** Optional prompt injected into the agent's system message when this tool is attached */
  tool_instructions: string | null;
}

export const NATIVE_TOOL_SCHEMAS: Record<string, NativeToolSchema> = {
  "close_conversation": {
    "tool_config": {
      "name": "close_conversation",
      "description": "Closes the current conversation and marks it as resolved",
      // Non-strict, mirroring the `update_kanban_status` sibling below: under strict
      // mode there are no optional fields, so `outcome` outside `required` makes the
      // provider reject the definition and `outcome` inside `required` forces the
      // model to emit it on every call. Requiredness is enforced SERVER-side instead
      // (ops `outcomeRequired`) — and it must stay out of `required` here, or
      // validateAndSanitizeToolParams would refuse the "retry without an outcome"
      // recovery path a re-close depends on.
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "outcome": {
            "type": "string",
            "enum": "[KANBAN_OUTCOME_VALUES]",
            "description": "Required only when the hub's terminal (conversation-ending) status declares outcomes: the closing outcome. Available outcomes: [KANBAN_OUTCOME_NAMES]"
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when the user's issue has been fully resolved."
  },
  "transfer_to_team": {
    "tool_config": {
      "name": "transfer_to_team",
      "description": "Hand off conversation to a human support team",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "team_name": {
            "type": "string",
            "description": "Name of the team to transfer to"
          }
        },
        "required": [
          "team_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when the user needs assistance that requires human intervention. The team will receive the full conversation history."
  },
  "delegate_to_hub": {
    "tool_config": {
      "name": "delegate_to_hub",
      "description": "Delegate a request to another hub. The target hub handles it as a task conversation and returns the result asynchronously.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "request": {
            "type": "string",
            "description": "The request to delegate to the target hub, phrased as a self-contained task."
          }
        },
        "required": [
          "request"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to hand a self-contained request to a configured partner hub. The target hub is set by an admin on the tool; you do not choose it. The result comes back asynchronously — do not wait for it in this turn."
  },
  "start_consult_thread": {
    "tool_config": {
      "name": "start_consult_thread",
      "description": "Ask the consultant configured on this tool a question, in a consult thread the support team can see. The answer may come back in this turn or later.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "question": {
            "type": "string",
            "description": "What you need from the consultant, phrased so it can be answered without further back-and-forth."
          },
          "title": {
            "type": "string",
            "description": "A short label for the consult thread (a few words), so the support team can tell parallel consults apart."
          }
        },
        "required": [
          "question",
          "title"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when answering needs knowledge you do not have and a consultant is configured for it. The consultant is set by an admin; you do not choose it. If the result says the answer will follow, END YOUR TURN — tell the customer you are checking and will come back to them. You will be brought back automatically when the answer arrives; do not poll or call this tool again for the same question."
  },
  "update_kanban_status": {
    "tool_config": {
      "name": "update_kanban_status",
      "description": "Updates the kanban status of the current conversation to track its progress through workflow stages",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "new_kanban_status": {
            "type": "string",
            "enum": "[KANBAN_STATUS_VALUES]",
            "description": "The new kanban status for the conversation. Available statuses: [KANBAN_STATUS_NAMES]"
          },
          "outcome": {
            "type": "string",
            "enum": "[KANBAN_OUTCOME_VALUES]",
            "description": "Required only when moving to the terminal (conversation-ending) status: the closing outcome. Available outcomes: [KANBAN_OUTCOME_NAMES]"
          },
          "scheduled_event_date": {
            "type": "string",
            "description": "Scheduled event date in RFC3339 format with timezone (e.g., \"2024-01-01T00:00:00-03:00\")"
          },
          "event_description": {
            "type": "string",
            "description": "Description of the scheduled event"
          },
          "event_sid": {
            "type": "string",
            "description": "External event ID for integration purposes"
          }
        },
        "required": [
          "new_kanban_status"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to update the kanban status of a conversation to reflect its current stage in the workflow."
  },
  "schedule_followup": {
    "tool_config": {
      "name": "schedule_followup",
      "description": "Schedules a custom manual followup for the current conversation at an exact future time. The system automatically determines the receiver based on conversation status and hub AI mode.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "scheduled_time": {
            "type": "string",
            "description": "Exact time to execute the followup in ISO 8601 format with timezone (e.g., \"2024-01-01T14:30:00-03:00\"). Must be in the future."
          },
          "message": {
            "type": "string",
            "description": "The followup message to send to the customer. Can include AI instructions in brackets like: \"Message text [AI Context: instructions for AI]\""
          }
        },
        "required": [
          "scheduled_time",
          "message"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to schedule a custom followup message at an exact future time. The caller is responsible for choosing appropriate timing (business hours, etc.). This is separate from automatic kanban-based followups."
  },
  "transfer_to_agent": {
    "tool_config": {
      "name": "transfer_to_agent",
      "description": "Hand off conversation to another AI agent",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "agent_name": {
            "type": "string",
            "description": "Name of the AI agent to transfer to"
          }
        },
        "required": [
          "agent_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when you need to hand the conversation to another agent on the same track — a specialist for a specific domain, or back to the entry pilot/copilot acting as a router for a request outside your domain."
  },
  "consult_agent": {
    "tool_config": {
      "name": "consult_agent",
      "description": "Consult an advisor agent for specialized knowledge without transferring the conversation",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "agent_name": {
            "type": "string",
            "description": "Name of the advisor agent to consult for advice"
          },
          "consult_question": {
            "type": "string",
            "description": "The specific question to ask the advisor agent"
          }
        },
        "required": [
          "agent_name",
          "consult_question"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when you need expert advice from an advisor agent but want to maintain conversation ownership. The consulted advisor will provide advice that you can use to help the user."
  },
  "read_file": {
    "tool_config": {
      "name": "read_file",
      "description": "Retrieve a file by path (preferred) or by ID. The file is injected into the conversation context for AI processing.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "Path to the file. Documents of your knowledge base live under \"data/<file-type>/\", exactly as list_files lists them — e.g. \"data/policies/eu/refunds.md\" — always written in full, and come back with the version the file is at: as text, or as base64 when the file is not text. Skill files live under \"skills/\", exactly as listed in your skill structure or in a file listing — e.g. \"skills/order-processing/references/menu.md\", where the segment after \"skills/\" names the skill. Files attached to this conversation live under \"conversation/\" and are announced in the transcript by that path — e.g. \"conversation/report.pdf\". A shorter form also works for those and is matched across everything you can read, preferring an exact path over one that merely ends with what you wrote (\"references/menu.md\", or just \"menu.md\"); dropping a middle segment does not resolve, and a bare name carried by both a skill and this conversation is reported as ambiguous so you can retry with the full path. Files from an EARLIER conversation with the same user live under \"conversations/<conversation_id>/\" — e.g. \"conversations/1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed/receipt.pdf\", exactly as the transcript of that conversation announces them (get_conversation). That form takes no shortening: write the mount, the conversation id and the file name in full. Prefer this over file_id: paths are stable, while IDs are regenerated whenever a hub is published."
          },
          "file_id": {
            "type": "string",
            "description": "The file ID to retrieve, from a conversation message or a file listing. Still supported, but prefer path for resource files."
          },
          "skill_id": {
            "type": "string",
            "description": "Optional. Restricts a path to one skill, named by its id or its slug — use it when the same file name exists in more than one skill and the tool reports the reference as ambiguous. Must be a skill linked to this agent."
          },
          "view_range": {
            "type": "array",
            "items": {
              "type": "integer"
            },
            "description": "Optional, for a text file: [start_line, end_line] returns only those lines, counted from 1 with both included; an end_line of -1 reads to the end of the file. Use it for a long file, or to read on where a truncated read stopped."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read a file for AI analysis. Provide either path (preferred — the exact paths appear in your resource structure and in list_files output) or file_id. The file will be injected into the conversation context."
  },
  "write_file": {
    "tool_config": {
      "name": "write_file",
      "description": "Create a document under \"data/<file-type>/\", or replace one whole. Without version it only creates: a taken path is refused. With the version you read, it replaces that version only, and is refused if the file changed since: read it again and redo your change. Returns the file's new version. Text only, at most 60,000 characters; to change part of a file, use edit_file.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "The document's full path, e.g. \"data/policies/eu/refunds.md\", as list_files gives it. Files outside \"data/\" (skills, resources, conversation files) are read-only."
          },
          "content": {
            "type": "string",
            "description": "The whole file, as text."
          },
          "version": {
            "type": "integer",
            "description": "The version you read, to replace that file. Omit it to create a new file."
          }
        },
        "required": [
          "path",
          "content"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to create a document under data/ or rewrite one whole: read it first and pass its version to replace it."
  },
  "edit_file": {
    "tool_config": {
      "name": "edit_file",
      "description": "Replace text in a document under \"data/<file-type>/\". old_str must match the file exactly, whitespace included, and occur once unless replace_all is set; otherwise nothing changes. The edit is made on the version you read, and is refused if the file changed since: read it again and redo the edit. Returns the file's new version. Text files of at most 60,000 characters.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "The document's full path, e.g. \"data/policies/eu/refunds.md\", as list_files gives it. Files outside \"data/\" (skills, resources, conversation files) are read-only."
          },
          "old_str": {
            "type": "string",
            "description": "The exact text to replace, with enough around it to occur once."
          },
          "new_str": {
            "type": "string",
            "description": "Its replacement; \"\" deletes old_str."
          },
          "version": {
            "type": "integer",
            "description": "The version read_file gave you."
          },
          "replace_all": {
            "type": "boolean",
            "description": "Replace every occurrence of old_str. Default false."
          }
        },
        "required": [
          "path",
          "old_str",
          "new_str",
          "version"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to change part of a document under data/: read it with read_file, then pass the exact text, its replacement and the version you read."
  },
  // The code sandbox (wayai#5083 Phase 3). The figures in its description are the backend's
  // sandbox limits (`apps/backend/src/sandbox/limits.ts`), pinned there by a test, so the model is
  // told the limits the sandbox enforces.
  "bash": {
    "tool_config": {
      "name": "bash",
      "description": "Run a Bash command in this conversation's sandbox: a disposable Linux container (Debian, with Python 3, curl, jq, zip and other common tools) whose working directory is /workspace. The sandbox starts on the first command and keeps its files between commands in this conversation, but it ends after 10 minutes without use or 60 minutes in all; the next command then starts a new one with the /workspace files from the last save, made when a sandbox stopped and at the end of each turn that ran a command, and its result says what came back. Only files under /workspace are kept, never a running process: a long job should write its progress to files under /workspace, so the work can go on from them. HOME is /workspace, so a pip install --user, or a virtual environment under /workspace, is kept too; a plain pip install is not. Installs count toward the saved workspace's size, and when it is too large to keep, what is under /workspace/.local is left out first. What the sandbox can reach over the network is set for this agent by a hub admin; with no network, nothing leaves it. A command runs for at most timeout_seconds and never past the turn's own time limit. Long output keeps only its first and last parts. The result gives the exit code, stdout and stderr.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "command": {
            "type": "string",
            "description": "The Bash script to run, as with `bash -c`. It runs in /workspace unless it changes directory."
          },
          "timeout_seconds": {
            "type": "integer",
            "description": "How long the command may run before it is killed, in seconds: 120 by default, at most 540.",
            "minimum": 1,
            "maximum": 540
          }
        },
        "required": [
          "command"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  },
  "copy_to_sandbox": {
    "tool_config": {
      "name": "copy_to_sandbox",
      "description": "Copy one of this conversation's files, or one of your documents, into the code sandbox, so commands run with bash can read or edit it there. path names the file as read_file does: this conversation's attachments under \"conversation/\" (e.g. \"conversation/report.xlsx\"), an earlier conversation's under \"conversations/<conversation_id>/<name>\", or a document under \"data/<file-type>/\" (e.g. \"data/contracts/acme/msa.docx\"), whose result gives the version you copied: pass it to copy_from_sandbox to copy the document back. Hub resource files cannot be copied. The copy lands at sandbox_path, replacing any file there; a file larger than 25 MiB is refused, and a document larger than 1 MiB. If no sandbox is running, the copy starts one, with the /workspace files from the last save.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "The file to copy, as read_file names it: \"conversation/<name>\" (a shorter unambiguous form or the file's id also works), \"conversations/<conversation_id>/<name>\", or a document's full path \"data/<file-type>/<path>\"."
          },
          "sandbox_path": {
            "type": "string",
            "description": "Where the copy lands in the sandbox, under /workspace (a relative path is taken from /workspace). Defaults to the file's name in /workspace."
          }
        },
        "required": [
          "path"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  },
  "copy_from_sandbox": {
    "tool_config": {
      "name": "copy_from_sandbox",
      "description": "Copy a file out of the code sandbox into this conversation, so what your commands produced (a chart, a converted document) is kept after the sandbox ends. The file is stored for the support team on an internal note and is NOT sent to the customer: the result gives its path under \"conversation/\", and send_files with that path delivers it when it should reach them. A file larger than 25 MiB is refused. With path, the file is written instead to one of your documents under \"data/<file-type>/\", with the version you copied it in at: the write goes through only while that is still the document's current version, so a document changed since is refused, and only where you may write documents. A document holds at most 1 MiB.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "sandbox_path": {
            "type": "string",
            "description": "The file to copy, under /workspace (a relative path is taken from /workspace)."
          },
          "name": {
            "type": "string",
            "description": "The stored file's name, with no folders (e.g. \"q3-report.xlsx\"). Defaults to the file's name in the sandbox. Not with path."
          },
          "path": {
            "type": "string",
            "description": "A document to write the file to instead, by its full path, e.g. \"data/contracts/acme/msa.docx\", as copy_to_sandbox or list_files gives it. Leave it out to keep the file with this conversation."
          },
          "version": {
            "type": "integer",
            "description": "With path: the version copy_to_sandbox (or read_file) gave for the document, to replace it. Leave it out to create a new document."
          }
        },
        "required": [
          "sandbox_path"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  },
  "send_files": {
    "tool_config": {
      "name": "send_files",
      "description": "Send files to the end user through their conversation channel (App, WhatsApp, email, etc.)",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "file_ids": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Files to send to the user, each named by path (preferred) or by ID. Paths are the addresses announced in the transcript and in your skill structure — \"conversation/report.pdf\" for a file attached to this conversation, \"conversations/<conversation_id>/receipt.pdf\" for one attached to an earlier conversation with the same user, \"skills/order-processing/price-list.pdf\" for a skill file. IDs remain accepted, but they are regenerated whenever a hub is published, so a path is the stable address."
          },
          "folder_ids": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Array of resource folder IDs to send all files from. Alternative to file_ids."
          },
          "message_text": {
            "type": "string",
            "description": "Message text to accompany the files. For WhatsApp, this becomes the caption on the last file. For email, this becomes the email body."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to send files to users. WhatsApp sends files individually with optional caption on last file. Email sends all files as attachments in a single email."
  },
  "list_files": {
    "tool_config": {
      "name": "list_files",
      "description": "Browse the files you can reach, by path. Call it with no path to see the mounts, then pass a mount or a folder path to list what is inside it. Every row carries the exact path read_file accepts, and send_files too outside \"data/\".",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "What to list. Omit it (or pass \"\") for the mounts you can browse — the available mounts are listed here at runtime. \"conversation\" lists the files attached to this conversation. \"skills\" lists every skill mount. \"skills/<skill-slug>\" lists the top level of a skill, and \"skills/<skill-slug>/<folder>/<subfolder>\" lists that folder. Folder rows come back with their own path, so you drill down by passing back the path of the row you want. \"data\" lists your document mounts, and \"data/<file-type>\" or \"data/<file-type>/<folder>\" lists every document beneath it, a page at a time."
          },
          "query": {
            "type": "string",
            "description": "Search text. Matched against a file's title and its file name. Under a resource or data/ path this searches the WHOLE subtree below it, not just the level named, and every result is returned as a full path."
          },
          "tags": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Return files carrying ANY of these tags. Applies to resource and data/ files — conversation attachments carry no tags. Searches the whole subtree, like query."
          },
          "metadata_filter": {
            "type": "object",
            "description": "Filter resource and data/ files by their metadata using MongoDB-style operators. Supported: $eq (equal), $ne (not equal), $gt/$gte/$lt/$lte (comparisons), $in/$nin (in/not in array), $contains (substring for text, membership for a list), $and/$or/$not (logical). Nested paths like \"location.city\" are supported, and a bare value means equality. Examples: {\"status\": \"active\"}, {\"priority\": {\"$gte\": 5}}, {\"location.city\": \"Miami\"}, {\"$and\": [{\"status\": \"active\"}, {\"priority\": {\"$gt\": 3}}]}, {\"$not\": {\"status\": \"archived\"}}. A comparison between different types simply does not match. Under data/, the file type's metadata schema refuses a field it does not declare. Searches the whole subtree, like query."
          },
          "limit": {
            "type": "integer",
            "description": "Maximum number of rows to return (default: 50, max: 100). The response reports the total the page was drawn from; under data/ it reports has_more instead, true when a full page may have more after it."
          },
          "offset": {
            "type": "integer",
            "description": "Number of rows to skip for pagination (default: 0)"
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to find out which files exist before reading one. Call it with no path first: that returns the mounts — \"conversation\" for what the user attached here, one \"skills/<slug>\" per skill linked to you, and one \"data/<file-type>\" per document type your toolset reaches. Then pass a mount or folder path back to list its contents. Every row carries a `path`; pass that path to read_file to open the file, or (outside \"data/\") to send_files to deliver it. Pass query, tags or metadata_filter to search a whole skill or data/ file type at once instead of walking it folder by folder. Files from EARLIER conversations are not listed here — the transcript of each past conversation announces its own files by path (get_conversation)."
  },
  "get_tool_schema": {
    "tool_config": {
      "name": "get_tool_schema",
      "description": "MANDATORY FIRST STEP: Retrieves the complete parameter schema for any tool by name. You MUST call this before using ANY tool to get its exact required parameters. This ensures execute_tool will succeed.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "tool_name": {
            "type": "string",
            "description": "The name of the tool to retrieve the schema for"
          }
        },
        "required": [
          "tool_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Mandatory first step before execute_tool. Returns `function_schema` for the target tool — pass `function_schema.parameters` (same shape, same keys) as the `parameters` envelope in your execute_tool call."
  },
  "execute_tool": {
    "tool_config": {
      "name": "execute_tool",
      "description": "Executes any tool by name. You MUST call get_tool_schema first for the target tool to learn its parameter shape, then pass that shape verbatim under `parameters`.",
      "strict": false,
      "parameters": {
        "type": "object",
        "required": [
          "tool_name",
          "parameters"
        ],
        "properties": {
          "tool_name": {
            "type": "string",
            "description": "The name of the tool to execute."
          },
          "parameters": {
            "type": "object",
            "description": "The arguments object for the target tool. Must match `function_schema.parameters` returned by get_tool_schema — same keys, same shape, same required fields. Inner contents are validated by the target tool, not by execute_tool."
          }
        },
        "additionalProperties": false
      }
    },
    "tool_instructions": "MANDATORY WORKFLOW: 1) Call get_tool_schema(tool_name) to receive the target's `function_schema`. 2) Build a `parameters` object that matches `function_schema.parameters` — same property keys, same types, all required fields. 3) Call execute_tool({ tool_name, parameters }). The `parameters` envelope here is identical in shape to `function_schema.parameters`."
  },
  "expand_summary": {
    "tool_config": {
      "name": "expand_summary",
      "description": "Retrieve the original messages of a section in the conversation summary. The summary block at the top of the conversation lists sections with stable `id`s — pass one to get the verbatim messages it covers.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "section_id": {
            "type": "string",
            "description": "The `id` of the section from the <conversation_summary> block at the top of the conversation."
          }
        },
        "required": [
          "section_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when a summary section is too compressed for the question at hand — e.g. you need the user's exact wording, the precise text of a previous decision, or a tool result that the summary only references. If you pass an unknown `section_id`, the response will include `available_section_ids` so you can retry with a valid one."
  },
  "read_skill": {
    "tool_config": {
      "name": "read_skill",
      "description": "Read a skill's SKILL.md content. Pass a skill_id from the skills linked to this agent (available ids are listed in the skill_id parameter).",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "skill_id": {
            "type": "string",
            "description": "The skill's resource_id. Must be one of the skills linked to this agent (the available ids are listed here at runtime)."
          }
        },
        "required": [
          "skill_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read a skill's instructions. Returns the full SKILL.md content as a string. Use read_skill_file to access other files referenced in the skill."
  },
  "read_skill_file": {
    "tool_config": {
      "name": "read_skill_file",
      "description": "Read a file from a skill by relative path. No need to call read_skill first if you already know the file path.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "skill_id": {
            "type": "string",
            "description": "The skill's resource_id. Must be one of the skills linked to this agent (the available ids are listed here at runtime)."
          },
          "file_path": {
            "type": "string",
            "description": "Relative path from skill root (e.g., \"references/menu.md\")"
          }
        },
        "required": [
          "skill_id",
          "file_path"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read files referenced in SKILL.md. Provide the relative path as shown in the skill content (e.g., \"references/menu.md\"). Returns content for text files or signed URL for binary files."
  },
  "update_state": {
    "tool_config": {
      "name": "update_state",
      "description": "Updates a configured state by merging updates with its current value. Scope (conversation vs user) is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to update. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          },
          "updates": {
            "type": "object",
            "description": "Object with key-value pairs to merge into the current state"
          }
        },
        "required": [
          "state_slug",
          "updates"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to track and update contextual information during conversations. Examples: updating cart items, recording form answers, saving user preferences. The updates are merged with existing state."
  },
  "get_state": {
    "tool_config": {
      "name": "get_state",
      "description": "Retrieves the current value of a configured state. Scope is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to retrieve. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          }
        },
        "required": [
          "state_slug"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to retrieve the current state value. This is useful when you need to check the current state before making decisions or when the state is not already available in the prompt context."
  },
  "reset_state": {
    "tool_config": {
      "name": "reset_state",
      "description": "Resets a configured state to its initial value as defined in the state schema. Scope is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to reset. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          }
        },
        "required": [
          "state_slug"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to reset state to its default initial value. This is useful when you need to clear accumulated state and start fresh, such as clearing a shopping cart or resetting form progress."
  },
  "get_conversations_summary": {
    "tool_config": {
      "name": "get_conversations_summary",
      "description": "Retrieves a timeline of past conversations with the current user, showing summaries and conversation IDs.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "max_conversations": {
            "type": "integer",
            "description": "Maximum number of past conversations to return (default: 10)"
          },
          "max_characters": {
            "type": "integer",
            "description": "Maximum characters in the output (default: 20000, max: 40000). Truncates oldest entries first."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool at the start of a conversation to retrieve the history of past conversations with this user. Returns summaries with conversation IDs that can be used with get_conversation for full transcripts."
  },
  "get_conversation": {
    "tool_config": {
      "name": "get_conversation",
      "description": "Retrieves the full transcript of a specific past conversation, plus the IDs of the conversations closed immediately before and after it. Use get_conversations_summary first to find conversation IDs.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "conversation_id": {
            "type": "string",
            "description": "The ID of the conversation to retrieve (from get_conversations_summary results)"
          },
          "max_characters": {
            "type": "integer",
            "description": "Maximum characters in the output (default: 30000, max: 60000). Truncates oldest messages first."
          }
        },
        "required": [
          "conversation_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read the full transcript of a specific past conversation. First use get_conversations_summary to find conversation IDs, then use this tool to drill into the details. The response may include previous_conversation_id and next_conversation_id — the conversations closed nearest in time either side of this one, which you can pass straight back to this tool to keep moving through the user's history. They are neighbours by closing time, not a fixed chain, and either may be absent."
  },

  // ---------------------------------------------------------------------------
  // Tools added to the TS catalog after the Supabase migration — schemas
  // reconstructed from the handler signatures in
  // apps/backend/src/tools/native/wayai/*.ts.
  // ---------------------------------------------------------------------------

  "set_state_path": {
    "tool_config": {
      "name": "set_state_path",
      "description": "Set a specific path within a state object (dot/array path) without overwriting the rest of the state. Scope is inferred from the state's definition.",
      // strict:false matches the other state ops so legacy composed-tool YAML
      // mapping `state_scope` (now ignored at runtime) doesn't trip OpenAI
      // strict-mode validators on in-flight calls.
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state variable to update. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          },
          "path": {
            "type": "array",
            "items": { "type": "string" },
            "description": "Path within the state object, as an ordered list of keys."
          },
          "value": {
            "description": "The value to write at the given path. Any JSON value is allowed."
          }
        },
        "required": ["state_slug", "path", "value"],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool when you need to update a single field inside a structured state object without replacing the entire state. Provide the full path from the root of the state object."
  },
  // NEVER OFFERED TO A MODEL, unlike every other entry in this file — and it is
  // `model_callable: false` on the catalog entry that delivers that, NOT the role
  // restriction beside it. An earlier version of this comment credited
  // `assignable_roles: ['monitor']`, which was wrong: a monitor runs a DISPATCHING
  // turn on `idle` (only the synchronous side-call sets `omit_tools`), and
  // `PATCH /api/setup/agents/:id` can re-role an agent that keeps the rows it
  // holds — so the role gate left the tool offerable on two paths.
  //
  // The schema exists because the catalog requires one per tool (the parity check
  // at the bottom of `native-tools.ts`) and because it is the one place the two
  // arguments are described for whoever writes the rule.
  //
  // `tool_instructions: null` FOLLOWS FROM THAT: instructions are injected into an
  // agent's system message when the tool is attached, and this tool is attached to
  // an agent whose model is never told it exists.
  // NEVER OFFERED TO A MODEL, like `insert_note` below and for the same reason:
  // `model_callable: false` on the catalog entry. A monitor's RULE selects it and
  // the monitor runtime carries it out — it never reaches `handleTool`, because
  // running a callee means re-entering the side-call with the caller's cascade
  // state, which no `ToolContext` carries.
  "run_monitor": {
    "tool_config": {
      "name": "run_monitor",
      "description": "Run another monitor on this hub. The named monitor judges the same conversation and acts on its own rules; nothing it finds is returned here.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "monitor_name": {
            "type": "string",
            "description": "The name of the monitor to run. It must be a monitor on this hub whose trigger is manual."
          }
        },
        "required": ["monitor_name"],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  },
  "insert_note": {
    "tool_config": {
      "name": "insert_note",
      "description": "Insert an admin-authored note into the answering agent's context for this turn only. The note is written by the administrator, not by a model; the monitor's variables are substituted into it.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "template": {
            "type": "string",
            "description": "The note text. Write {{variable_name}} to substitute one of this monitor's own variables. Unresolved names render as nothing and are recorded on the audit row."
          },
          "keep_in_history": {
            "type": "boolean",
            "description": "Default false: the note briefs this turn only and is never stored. Set true to also record it as an internal message the support team can see and the customer never receives."
          }
        },
        "required": ["template"],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  }
};
